import { Injectable } from '@angular/core';
import {
    IDisplayOptions,
    ItemType,
    ISuperTableCalculationFunction,
    ISuperTableComponent,
    ISuperTableRow,
    ITranslationEntry,
    SuperTableRowType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { AumicoStyleOptions, IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { ISuperTableOptions, IXAxisConfigColumn, SuperTableColumnType } from '@modeso/types__aumico-lib-notes-be';
import { IItem, RowTypes } from '../../../../aumico-lib-annualreport-fe/src/lib';
import {
    AumFinancialNumberPipe,
    DateLocalePipe,
    EMPTY_STRING,
    FIRST_ELEMENT_INDEX,
    NumberUtils,
} from '../../../../aumico-lib-core-fe/src/lib';
import { NotesNumberingHelper } from '../helpers/notes-numbering.helper';
import { SuperTableHelper } from '../helpers/super-table.helper';
import { FX_RESERVE_NODE_NAME, IFxNodeStamp, IFxNotesContext } from '../interfaces/IFxNotesContext.interface';
import { IFxPrintRow } from '../interfaces/fx-print-row.interface';
import { MappingItemsSelectionNode } from '../interfaces/mapping-items-selection-node.interface';
import { ISuperTablePrintPreviewLiveTrees } from '../interfaces/super-table-print-preview-live-trees.interface';
import { INotesSubSectionPrintPreviewComponent } from '../interfaces/notes-subsection-print-preview.interface';
import { INotesSuperTableColumnForPrintPreview } from '../interfaces/notes-super-table-column-for-print-preview.interface';
import { INotesSuperTableDataForPrintPreview } from '../interfaces/notes-super-table-data-for-print-preview.interface';
import { INotesSuperTableRowForPrintPreview } from '../interfaces/notes-super-table-row-for-print-preview.interface';
import { SuperTableMapper } from './super-table.mapper';
import { SuperTableColumnWidth } from '../enums/SuperTableColumnWidth.ebum';
import { SuperTableColumnBelongingYear } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/super_table_column_belonging_year.enum';

// Re-exported so existing importers keep working; declared in interfaces/ so the builder
// (SuperTableMapper) can reference it without importing this mapper back.
export { ISuperTablePrintPreviewLiveTrees } from '../interfaces/super-table-print-preview-live-trees.interface';

const TWO_DECIMAL_PLACES = 2;
const ZERO_DECIMAL_PLACES = 0;
const FIVE_DECIMAL_PLACES = 5;
const SPACE = ' ';
const ORGANIZATION_IDS_WITH_KENDRIS_SUPER_TABLE_FORMATTING = [
    '6748895248c324641ba05177',
    '69a6acb07ec10d63acb3d9cd',
    '6a5def1421500ed44a2a7752',
    '6a5df00221500ed44a2a7808',
    '6ad8c2c5e81d241f0582639a',
    '6ad8ce27a193a5e31c18459a',
    '6ad8cd6ae4273640b0e7d0ba',
    '6ad8c00ae81d241f058257a4',
];
const getColumnById = (
    columns: INotesSuperTableColumnForPrintPreview[],
    columnId: string,
): INotesSuperTableColumnForPrintPreview | undefined => {
    return columns.find((column) => column.columnId === columnId);
};

@Injectable({
    providedIn: 'root',
})
export class NotesDisplayMapper {
    constructor(
        private readonly dateLocalPipe: DateLocalePipe,
        private readonly aumFinancialNumberPipe: AumFinancialNumberPipe,
        private readonly superTableHelper: SuperTableHelper,
        private readonly superTableMapper: SuperTableMapper,
        private readonly notesNumberingHelper: NotesNumberingHelper,
    ) { }

    /**
     * REV-20899: combine the imported balance with the aggregated transactional
     * reclass so super-table FLI / SubFLI / group / account values reflect the
     * "final" reclass-applied number in the print preview. `closingReClass` is
     * populated on leaves by `applyReclassificationsToFinStatements` (provider
     * boundary) and aggregated up by `GroupingHelper.recalculateGroupSummaries`
     * — phantom reclasses accounts contribute via both layers.
     */
    private finalCYValue(data: { calculatedClosingBalance?: number | null; closingReClass?: number | null } | null | undefined): number | undefined {
        if (data == null) return undefined;
        const balance = data.calculatedClosingBalance;
        const reclass = data.closingReClass;
        if (balance == null && (reclass == null || reclass === 0)) return balance ?? undefined;
        return (balance ?? 0) + (reclass ?? 0);
    }

    private finalPYValue(data: { calculatedOpeningBalance?: number | null; openingReClass?: number | null } | null | undefined): number | undefined {
        if (data == null) return undefined;
        const balance = data.calculatedOpeningBalance;
        const reclass = data.openingReClass;
        if (balance == null && (reclass == null || reclass === 0)) return balance ?? undefined;
        return (balance ?? 0) + (reclass ?? 0);
    }

    /**
     * REV-20899: when the print preview is rendered, super-table rows hold
     * `currentValue` / `previousValue` snapshots persisted at the time of the
     * last super-table form save. If reclasses (or phantom reclasses accounts)
     * were added afterwards, those snapshots are stale.
     *
     * This method replays the same row-value derivation the super-table form
     * uses live (`finalCYValue` / `finalPYValue` over the matching mapping
     * nodes), against the latest reclass-applied trees, and overwrites
     * `row.currentValue` / `row.previousValue` on a CLONE of the input data so
     * the caller never observes side effects.
     *
     * Rows without mapped items (titles, spacers, text rows, formula-only
     * calculation rows) are left untouched.
     */
    /**
     * FX (REV-21512): the Excel export needs cells, not the rendered HTML the PDF path carries,
     * so it rebuilds the super table itself — but the display-currency twins and the rate rows
     * only exist once this recompute has run. Exposed so the export derives them from exactly
     * the same pass the preview renders, instead of a second implementation.
     */
    public applyFxTwinsForExport(
        superTableData: ISuperTableComponent,
        trees: ISuperTablePrintPreviewLiveTrees,
        fxContext: IFxNotesContext,
    ): ISuperTableComponent {
        return this.recomputeSuperTableRowValuesForPrintPreview(superTableData, trees, fxContext);
    }

    private recomputeSuperTableRowValuesForPrintPreview(
        superTableData: ISuperTableComponent,
        trees: ISuperTablePrintPreviewLiveTrees,
        fxContext: IFxNotesContext | null = null,
    ): ISuperTableComponent {
        if (trees == null || (trees.balance == null && trees.income == null)) return superTableData;
        if (superTableData == null || !Array.isArray(superTableData.rows) || superTableData.rows.length === 0) return superTableData;

        // Combined tree for rows that don't fix a financialType — same shape
        // the super-table form uses (`balance.children.concat(income)`).
        const combinedTree: MappingItemsSelectionNode | null = (() => {
            const balanceChildren = trees.balance?.children ?? [];
            const incomeChild = trees.income;
            if (balanceChildren.length === 0 && incomeChild == null) return null;
            return {
                data: null,
                children: incomeChild != null ? balanceChildren.concat(incomeChild) : balanceChildren,
                type: ItemType.GROUP,
                financialType: null,
                expanded: true,
            } as MappingItemsSelectionNode;
        })();

        const cloned: ISuperTableComponent = {
            ...superTableData,
            rows: superTableData.rows.map((r) => ({ ...r })),
        };

        for (const row of cloned.rows) {
            if (row == null) continue;
            // Only rows that are tied to mapping items can be recomputed —
            // titles, spacers, text rows, and pure formula calculation rows
            // either have no items or are computed by the column formula
            // pipeline elsewhere.
            if (row.type === SuperTableRowType.TITLE) continue;
            if (row.type === SuperTableRowType.SPACE) continue;
            if (row.type === SuperTableRowType.TEXT_ROW) continue;
            if (row.type === SuperTableRowType.CALCULATION) continue;
            // FX (REV-21512): rate rows never carry stored values — closing/average rates come
            // straight from the context; position rates fall through to the tree matching below.
            if (row.type === SuperTableRowType.RATE) {
                if (fxContext == null) continue;
                if (row.rateSource === 'closing') {
                    (row as IFxPrintRow).__fxPrintCY = fxContext.closingRateCY;
                    (row as IFxPrintRow).__fxPrintPY = fxContext.closingRatePY;
                    continue;
                }
                if (row.rateSource === 'average') {
                    (row as IFxPrintRow).__fxPrintCY = fxContext.averageRateCY;
                    (row as IFxPrintRow).__fxPrintPY = fxContext.averageRatePY;
                    continue;
                }
            }
            if (!Array.isArray(row.itemsIds) || row.itemsIds.length === 0) continue;

            let tree: MappingItemsSelectionNode | null = null;
            if ((row as any).financialType === 'balance') {
                tree = trees.balance;
            } else if ((row as any).financialType === 'income') {
                tree = trees.income;
            } else {
                tree = combinedTree;
            }
            if (tree == null) continue;

            const matchingNodes = this.superTableMapper.findMatchingNodesForRowItems(row as any, tree);
            if (matchingNodes == null || matchingNodes.length === 0) continue;

            // FX (REV-21512): position rate = Σdisplay ÷ Σfunctional over the matched stamps
            // (03b formula, «–» when the functional total is 0) — same as the editor.
            if (row.type === SuperTableRowType.RATE) {
                (row as IFxPrintRow).__fxPrintCY = this.fxPositionRateOf(matchingNodes, 'cy');
                (row as IFxPrintRow).__fxPrintPY = this.fxPositionRateOf(matchingNodes, 'py');
                continue;
            }

            // FX (REV-21512): the display-currency twin of the row — identical semantics to the
            // editor's recomputeFxTwinValues (all stamps present or the cell stays empty).
            if (fxContext != null) {
                (row as IFxPrintRow).__fxPrintCY = this.fxStampSumOf(matchingNodes, 'cy');
                (row as IFxPrintRow).__fxPrintPY = this.fxStampSumOf(matchingNodes, 'py');
            }

            // FX (REV-21512): the translation reserve exists only in display currency — its
            // functional cells stay EMPTY, never 0 (the reserve arises from translating).
            const onlyReserveNodes = matchingNodes.every(
                (n) => (n?.data as { name?: string })?.name === FX_RESERVE_NODE_NAME,
            );
            if (onlyReserveNodes) continue;

            const cyValues = matchingNodes.map((n) => this.finalCYValue(n?.data as any));
            const pyValues = matchingNodes.map((n) => this.finalPYValue(n?.data as any));

            row.currentValue = NumberUtils.sumIgnoringInfiniteNumbers(cyValues as number[], 0);
            row.previousValue = NumberUtils.sumIgnoringInfiniteNumbers(pyValues as number[], 0);
        }

        // FX (REV-21512): calculation rows run the same functions over the twin column —
        // identical semantics to the editor's recomputeFxTwinValues.
        if (fxContext != null) {
            const calculationTwins = this.superTableHelper.computeFxCalculationRowValues(
                cloned.rows,
                cloned.calculations,
                (row, year) => this.fxPrintOperandOf(row as ISuperTableRow, year),
            );
            for (const row of cloned.rows) {
                const twin = row.rowId != null ? calculationTwins.get(row.rowId) : undefined;
                if (twin != null) {
                    (row as IFxPrintRow).__fxPrintCY = twin.cy;
                    (row as IFxPrintRow).__fxPrintPY = twin.py;
                }
            }
        }

        return cloned;
    }

    /** FX (REV-21512): print-side twin of the editor's fxOperandValueOf. */
    private fxPrintOperandOf(row: ISuperTableRow, year: 'cy' | 'py'): number | null | undefined {
        if (SuperTableHelper.isFxUserValueRowType(row.type)) {
            const raw = row[
                year === 'cy' ? SuperTableColumnType.CURRENT_YEAR_FX : SuperTableColumnType.PREVIOUS_YEAR_FX
            ] as number | string | null | undefined;
            const parsed = typeof raw === 'number' ? raw : raw == null || raw === '' ? NaN : +raw;

            return NumberUtils.isFinite(parsed) ? parsed : undefined;
        }
        if (row.type === SuperTableRowType.SPACE) {
            return undefined;
        }

        return (row as IFxPrintRow)[year === 'cy' ? '__fxPrintCY' : '__fxPrintPY'] ?? null;
    }

    /** FX (REV-21512): `__fx` stamps of the matched nodes (nodes without a stamp are dropped). */
    private fxStampsOfNodes(matchingNodes: MappingItemsSelectionNode[]): IFxNodeStamp[] {
        return matchingNodes
            .map((node) => (node?.data as { __fx?: IFxNodeStamp })?.__fx)
            .filter((stamp): stamp is IFxNodeStamp => stamp != null);
    }

    private fxStampSumOf(matchingNodes: MappingItemsSelectionNode[], year: 'cy' | 'py'): number | null {
        const stamps = this.fxStampsOfNodes(matchingNodes);
        if (stamps.length === 0) {
            return null;
        }
        const values = stamps.map((stamp) => (year === 'cy' ? stamp.displayCY : stamp.displayPY));

        return values.some((value) => value == null)
            ? null
            : NumberUtils.sumIgnoringInfiniteNumbers(values as number[], 0);
    }

    private fxPositionRateOf(matchingNodes: MappingItemsSelectionNode[], year: 'cy' | 'py'): number | null {
        const stamps = this.fxStampsOfNodes(matchingNodes);
        if (stamps.length === 0) {
            return null;
        }
        const functional = NumberUtils.sumIgnoringInfiniteNumbers(
            stamps.map((stamp) => (year === 'cy' ? stamp.functionalCY : stamp.functionalPY)),
            0,
        );
        const displayValues = stamps.map((stamp) => (year === 'cy' ? stamp.displayCY : stamp.displayPY));
        const display = displayValues.some((value) => value == null)
            ? null
            : NumberUtils.sumIgnoringInfiniteNumbers(displayValues as number[], 0);

        return display == null || functional === 0 ? null : display / functional;
    }

    /**
     * Insert a saved translation for one super-table text. Mirrors the print-page visitor's `resolve`
     * but never reports (reporting stays in the visitor): hand-correction wins, then a real fallback
     * (the target-language locale value, for locale-object titles), then the AI block, else the source.
     * Free-text spots pass no fallback; the source text is the block key.
     */
    private resolve(source: unknown, translations?: Record<string, ITranslationEntry>, fallback?: string): string {
        if (typeof source !== 'string') {
            return fallback ?? (source as string);
        }
        // Not only the AI block: the print page merges the mapping's translation of the same text in ahead of it.
        const entry = translations?.[source];
        if (entry?.hasUserEdited) {
            return entry.value;
        }
        if (fallback != null && fallback !== EMPTY_STRING && fallback !== source) {
            return fallback;
        }
        if (entry) {
            return entry.value;
        }
        return fallback ?? source;
    }

    mapNotesSuperTableToPrintPreviewData(
        superTableData: ISuperTableComponent,
        currentYearDate: string,
        previousYearDate: string,
        displayOptions: IDisplayOptions,
        componentTitle: IDynamicKeyValue | undefined,
        translationLocale: string,
        hasFootnotes: boolean,
        organizationId?: string,
        liveMappingTrees?: ISuperTablePrintPreviewLiveTrees,
        fxContext: IFxNotesContext | null = null,
        // Saved translations for the active language (source text → entry). The super-table is scraped
        // from this live DOM, so its translations are inserted here, not by the print-page visitor.
        translations?: Record<string, ITranslationEntry>,
        // Report language — the block key for locale-object titles (segment/column) is title[reportLocale].
        reportLocale?: string,
    ): INotesSuperTableDataForPrintPreview {
        // REV-20899: if the caller supplies live (reclass-applied) mapping
        // trees, replay the form's value derivation against them so the
        // print preview reflects any reclasses / phantom reclasses accounts
        // added after the super-table was last saved.
        // FX (REV-21512): with an FX context the same pass also derives the
        // display-currency twin values and rate-row rates from the stamped trees.
        if (liveMappingTrees != null) {
            superTableData = this.recomputeSuperTableRowValuesForPrintPreview(
                superTableData,
                liveMappingTrees,
                fxContext,
            );
        }
        const superTableDataForPrint: INotesSuperTableDataForPrintPreview = {
            rows: [],
            columns: [],
            segments: [],
            transpose: superTableData.options.transpose,
            componentTitle: componentTitle ? `${componentTitle[translationLocale]}` : EMPTY_STRING,
            previousYear: null,
            footnotes: hasFootnotes ? (superTableData.footnotes ?? []).map((note) => this.resolve(note, translations)) : [],
            spacerColumns: [],
            showSpacerBeforeCalculationRows: false,
        };

        // Create sets of invalid calculation IDs for quick lookup
        const invalidRowCalculations = new Set<string>();
        const invalidColumnCalculations = new Set<string>();

        // Check calculations for invalidRef and categorize them
        if (superTableData.calculations) {
            for (const calc of superTableData.calculations) {
                // Type cast to include invalidRef property
                const calcWithInvalidRef = calc as any;
                if (calcWithInvalidRef.invalidRef === true) {
                    // Check if this calcId matches a row or column
                    const isRowCalc = superTableData.rows.some(row => row.rowId === calc.calcId);
                    const isColumnCalc = superTableData.options.xAxisConfig.some(col => col.columnId === calc.calcId);

                    if (isRowCalc) {
                        invalidRowCalculations.add(calc.calcId);
                    }
                    if (isColumnCalc) {
                        invalidColumnCalculations.add(calc.calcId);
                    }
                }
            }
        }

        for (const segment of superTableData.options.segments) {
            superTableDataForPrint.segments.push({
                segmentTitle: this.resolve(
                    reportLocale != null ? segment.title[reportLocale] : undefined,
                    translations,
                    segment.title[translationLocale] || Object.values(segment.title)[FIRST_ELEMENT_INDEX],
                ),
                childColumnsIds: segment.childColIds,
                startColumnId: segment.childColIds[FIRST_ELEMENT_INDEX],
            });
        }
        // Numbering is written into the target-language title only; carry it over onto the resolved title.
        const { numbering: tableNumber, title: unnumberedTitle } = this.notesNumberingHelper.extractNumberingAndTitle(
            superTableData.options.title[translationLocale],
        );
        const tableNumberPrefix = tableNumber !== EMPTY_STRING ? `${tableNumber}${SPACE}` : EMPTY_STRING;
        // Same tab as the report language: that title is the numbered one, so use it without the number.
        const reportTitle =
            reportLocale === translationLocale
                ? unnumberedTitle
                : reportLocale != null
                    ? superTableData.options.title[reportLocale]
                    : undefined;
        // No title in any language stays empty — not the word "undefined" in the print.
        const tableTitleSource: string =
            reportTitle || Object.values(superTableData.options.title).find((value) => !!value) || EMPTY_STRING;
        superTableDataForPrint.columns.push({
            columnId: 't',
            // The report-language title is the block key; the target-language field is not used as a
            // fallback here — historic renders polluted it with numbered "undefined" strings.
            columnTitle: `${tableNumberPrefix}${this.resolve(tableTitleSource, translations)}`,
            type: SuperTableColumnType.TEXT,
        });

        const options: ISuperTableOptions = superTableData.options as ISuperTableOptions;

        for (const column of superTableData.options.xAxisConfig) {
            if (displayOptions.hidePreviousYear && column.belongingYear === SuperTableColumnBelongingYear.PREVIOUS_YEAR) {
                continue; // Skip this column if it's the previous year and should be hidden
            }
            // FX (REV-21512): twin columns print only when the caller supplies an FX context
            // (values are computed, not in the blob); functional columns are suppressed while
            // the table is in rate mode — same rendering the editor shows.
            if (SuperTableHelper.isFxColumnType(column.type) && fxContext == null) {
                continue;
            }
            superTableDataForPrint.columns.push({
                columnId: column.columnId,
                columnTitle: this.getColumnTitle(
                    column,
                    currentYearDate,
                    previousYearDate,
                    translationLocale,
                    options.showFiscalYear,
                    options.showCurrency,
                    fxContext?.displayCurrency,
                    translations,
                    reportLocale,
                ),
                type: column.type,
                belongingYear: column.belongingYear,
                isRatio:
                    column.type === SuperTableColumnType.CALCULATION &&
                    superTableData.calculations.find(
                        (calc) =>
                            calc.calcId === column.columnId && calc.function === ISuperTableCalculationFunction.RATIO,
                    ) != null,
                format: column.format,
                columnWidth: column.columnWidth,
            });
        }

        for (const row of superTableData.rows) {
            const printRow: INotesSuperTableRowForPrintPreview = {
                rowType:
                    row.type === SuperTableRowType.TITLE
                        ? RowTypes.TITLE_ROW
                        : row.type === SuperTableRowType.SPACE
                            ? RowTypes.EMPTY_ROW
                            : row.type === SuperTableRowType.CALCULATION
                                ? RowTypes.CALC_ROW
                                : RowTypes.ROW,
                t: this.resolve(row.title, translations),
                format: row.format,
            };

            for (const col of superTableData.options.xAxisConfig) {
                if (displayOptions.hidePreviousYear && col.belongingYear === SuperTableColumnBelongingYear.PREVIOUS_YEAR) {
                    continue; // Skip this column if it's the previous year and should be hidden
                }
                // FX (REV-21512): mirror the column loop above.
                const isFxColumn = SuperTableHelper.isFxColumnType(col.type);
                if (isFxColumn && fxContext == null) {
                    continue;
                }
                // FX (REV-21512): rate rows print at 5 decimal places, immune to the
                // hide-decimals print options — exactly what the editor shows.
                const isRateRow = row.type === SuperTableRowType.RATE;
                const { numberFormatValues, numberFormatRatios } = this.getNumberFormatAndDecimalPlaces(
                    displayOptions,
                    isRateRow ? false : displayOptions.notesHideDecimalPlacesValues,
                    isRateRow ? false : displayOptions.notesHideDecimalPlacesRatios,
                    isRateRow
                        ? FIVE_DECIMAL_PLACES
                        : this.superTableHelper.resolveEffectiveDecimalPlaces(row.format, col.format),
                );

                if (col.type === SuperTableColumnType.CURRENT_YEAR) {
                    printRow[col.columnId] = row.currentValue;
                } else if (col.type === SuperTableColumnType.PREVIOUS_YEAR) {
                    printRow[col.columnId] = row.previousValue;
                } else if (col.type === SuperTableColumnType.CURRENT_YEAR_FX) {
                    // FX (REV-21512): user rows print what was typed, everything else the
                    // computed twin — the same split the editor renders.
                    printRow[col.columnId] = SuperTableHelper.isFxUserValueRowType(row.type)
                        ? (row[col.columnId] as string | number)
                        : ((row as IFxPrintRow).__fxPrintCY as number);
                } else if (col.type === SuperTableColumnType.PREVIOUS_YEAR_FX) {
                    printRow[col.columnId] = SuperTableHelper.isFxUserValueRowType(row.type)
                        ? (row[col.columnId] as string | number)
                        : ((row as IFxPrintRow).__fxPrintPY as number);
                } else {
                    printRow[col.columnId] = row[col.columnId] as string | number;
                }

                // Text cells (title / text rows) hold user text — insert its saved translation.
                if (typeof printRow[col.columnId] === 'string') {
                    printRow[col.columnId] = this.resolve(printRow[col.columnId] as string, translations);
                }

                // Check if this cell should be marked as #REF! due to invalid calculations
                // But exclude title rows from showing #REF!
                const isInvalidRow = invalidRowCalculations.has(row.rowId);
                const isInvalidColumn = invalidColumnCalculations.has(col.columnId);

                if ((isInvalidRow || isInvalidColumn) && row.type !== SuperTableRowType.TITLE) {
                    printRow[col.columnId] = '#REF!';
                    continue; // Skip further processing for this cell
                }

                if (
                    row.type !== SuperTableRowType.TITLE &&
                    row.type !== SuperTableRowType.TEXT_ROW &&
                    (col.type === SuperTableColumnType.CURRENT_YEAR ||
                        col.type === SuperTableColumnType.PREVIOUS_YEAR ||
                        col.type === SuperTableColumnType.CURRENT_YEAR_FX ||
                        col.type === SuperTableColumnType.PREVIOUS_YEAR_FX ||
                        col.type === SuperTableColumnType.CALCULATION ||
                        col.type === SuperTableColumnType.NUMBER)
                ) {
                    const column: INotesSuperTableColumnForPrintPreview = getColumnById(
                        superTableDataForPrint.columns,
                        col.columnId,
                    );

                    if (
                        printRow[col.columnId] != null &&
                        !NumberUtils.isZero(printRow[col.columnId] as number) &&
                        (col.format?.styles[AumicoStyleOptions.FLIP_SIGN]?.enabled ||
                            row.format?.styles[AumicoStyleOptions.FLIP_SIGN]?.enabled)
                    ) {
                        printRow[col.columnId] = NumberUtils.multiplyNumbers([printRow[col.columnId] as number, -1]);
                    }

                    if (column.isRatio) {
                        printRow[col.columnId] =
                            printRow[col.columnId] != null && NumberUtils.isFinite(printRow[col.columnId] as number)
                                ? this.aumFinancialNumberPipe.transform(
                                    printRow[col.columnId] as number,
                                    numberFormatRatios,
                                    displayOptions.nonBreakingHyphen,
                                )
                                : '';
                    } else {
                        printRow[col.columnId] =
                            printRow[col.columnId] != null && NumberUtils.isFinite(printRow[col.columnId] as number)
                                ? this.aumFinancialNumberPipe.transform(
                                    printRow[col.columnId] as number,
                                    numberFormatValues,
                                    displayOptions.nonBreakingHyphen,
                                )
                                : '';
                    }

                    if (
                        printRow[col.columnId] != null &&
                        (col.format?.styles[AumicoStyleOptions.PERCENTAGE]?.enabled ||
                            row.format?.styles[AumicoStyleOptions.PERCENTAGE]?.enabled) &&
                        printRow[col.columnId]
                    ) {
                        printRow[col.columnId] = printRow[col.columnId] + ' %';
                    }

                    // FX (REV-21512): an uncomputable rate prints as «–», exactly like the editor.
                    if (isRateRow && isFxColumn && (printRow[col.columnId] == null || printRow[col.columnId] === '')) {
                        printRow[col.columnId] = '–';
                    }
                }
            }
            superTableDataForPrint.rows.push(printRow);
        }

        // Client-specific customization: Add spacer columns between numeric columns for certain organizations
        if (organizationId) {
            superTableDataForPrint.showSpacerBeforeCalculationRows = this.shouldUseKendrisSuperTableFormatting(
                organizationId,
            );
            superTableDataForPrint.spacerColumns = this.calculateSpacerColumns(
                superTableData,
                superTableDataForPrint,
                organizationId
            );
        }

        return superTableDataForPrint;
    }

    /**
     * Calculate which columns should have spacers after them for client-specific formatting.
     * Adds spacers between numeric columns when there's a calculation row (SUM/TOTAL) at the bottom.
     * @param superTableData The original super table data
     * @param superTableDataForPrint The print preview data being constructed
     * @param organizationId The organization ID for client-specific logic
     * @returns Array of column IDs after which spacers should be inserted
     */
    private calculateSpacerColumns(
        superTableData: ISuperTableComponent,
        superTableDataForPrint: INotesSuperTableDataForPrintPreview,
        organizationId: string,
    ): string[] {
        const spacerColumns: string[] = [];

        // Add your organization ID here for client-specific logic
        if (!this.shouldUseKendrisSuperTableFormatting(organizationId)) {
            return spacerColumns;
        }


        // Find consecutive numeric columns and add spacers between them
        const columns = superTableDataForPrint.columns.filter(col => col.columnId !== 't');
        
        for (let i = 0; i < columns.length - 1; i++) {
            const currentCol = columns[i];
            const nextCol = columns[i + 1];

            const isCurrentNumeric = currentCol.type !== SuperTableColumnType.TEXT;
            const isNextNumeric = nextCol.type !== SuperTableColumnType.TEXT;

            const isCurrentStandardWith = currentCol.columnWidth === SuperTableColumnWidth.STANDARD;
            const isNextStandardWith = nextCol.columnWidth === SuperTableColumnWidth.STANDARD;

            // Add spacer after current column if both current and next are numeric
            if ((isCurrentNumeric && isNextNumeric) || (isCurrentStandardWith && isNextStandardWith)) {
                spacerColumns.push(currentCol.columnId);
            }
        }

        return spacerColumns;
    }

    private shouldUseKendrisSuperTableFormatting(organizationId: string): boolean {
        return ORGANIZATION_IDS_WITH_KENDRIS_SUPER_TABLE_FORMATTING.includes(organizationId);
    }

    private getColumnTitle(
        column: IXAxisConfigColumn,
        currentYearDate: string,
        previousYearDate: string,
        translationLocale: string,
        showFiscalYear: boolean,
        showCurrency: boolean,
        fxDisplayCurrency?: string,
        translations?: Record<string, ITranslationEntry>,
        reportLocale?: string,
    ): string {
        // FX (REV-21512): twin columns carry the same date label as their functional original.
        const isFxColumn = SuperTableHelper.isFxColumnType(column.type);
        let result = '';
        if (
            (column.type === SuperTableColumnType.CURRENT_YEAR ||
                column.type === SuperTableColumnType.CURRENT_YEAR_FX) &&
            column.title == null
        ) {
            if (showFiscalYear) {
                // only the year YYYY
                result = new Date(currentYearDate).getFullYear().toString();
            } else {
                result = this.dateLocalPipe.transform(currentYearDate).toString();
            }
        } else if (
            (column.type === SuperTableColumnType.PREVIOUS_YEAR ||
                column.type === SuperTableColumnType.PREVIOUS_YEAR_FX) &&
            column.title == null
        ) {
            if (showFiscalYear) {
                // only the year YYYY
                result = new Date(previousYearDate).getFullYear().toString();
            } else {
                result = this.dateLocalPipe.transform(previousYearDate).toString();
            }
        } else {
            result = this.resolve(
                reportLocale != null ? column.title[reportLocale] : undefined,
                translations,
                column.title[translationLocale] || Object.values(column.title)[FIRST_ELEMENT_INDEX],
            );
        }

        // FX (REV-21512): unsaved twin columns fall back to the display currency, like the editor.
        const currencyLabel =
            column.currency != null
                ? this.resolve(column.currency, translations)
                : isFxColumn
                    ? fxDisplayCurrency
                    : undefined;
        if (showCurrency && currencyLabel) {
            result += '\n' + currencyLabel
        }

        return result;
    }

    mapPdfTronSubSectionItemsToNotesSubSectionComponentsForPrintPreview(
        pdfTronNotesItems: IItem[],
    ): INotesSubSectionPrintPreviewComponent[] {
        return pdfTronNotesItems.map((pdfTronNotesItem, index) => {
            return {
                componentId: `${pdfTronNotesItem.componentId}_${index}`,
                componentType: pdfTronNotesItem.itemType,
                data: pdfTronNotesItem.data,
                tables: pdfTronNotesItem.tables,
                componentTitle: pdfTronNotesItem.componentTitle,
                isFxFormat: pdfTronNotesItem.isFxFormat,
            };
        });
    }

    private getNumberFormatAndDecimalPlaces(
        printDisplayOptions: IDisplayOptions,
        hideDecimalPlacesValues: boolean,
        hideDecimalPlacesRatios: boolean,
        customDecimalPlaces?: number,
    ): {
        numberOfDecimalPlacesValues: number;
        numberFormatValues: string;
        numberOfDecimalPlacesRatios: number;
        numberFormatRatios: string;
    } {
        let numberOfDecimalPlacesValues = customDecimalPlaces != null ? customDecimalPlaces : TWO_DECIMAL_PLACES;
        let numberOfDecimalPlacesRatios = customDecimalPlaces != null ? customDecimalPlaces : TWO_DECIMAL_PLACES;
        let numberFormatValues = `1.${numberOfDecimalPlacesValues}-${numberOfDecimalPlacesValues}`;
        let numberFormatRatios = `1.${numberOfDecimalPlacesRatios}-${numberOfDecimalPlacesRatios}`;
        if (hideDecimalPlacesValues) {
            numberOfDecimalPlacesValues = ZERO_DECIMAL_PLACES;
            numberFormatValues = '1.0-0';
        }
        if (hideDecimalPlacesRatios) {
            numberOfDecimalPlacesRatios = ZERO_DECIMAL_PLACES;
            numberFormatRatios = '1.0-0';
        }

        return {
            numberOfDecimalPlacesValues,
            numberFormatValues,
            numberOfDecimalPlacesRatios,
            numberFormatRatios,
        };
    }
}
